import type { GettextInterface } from "@indeed/indeed-gettext";

import type { HiringEventData } from "../../../../../../hooks/useConditionalHiringEventsData";
import {
  type HiringEventLocation,
  type HiringEventNonPhysicalLocation,
  LocationTypeName,
} from "../../../constants/apiConstants";
import { splitTitleAndContent } from "../../../utils/htmlUtils";
import type { HiringEventsViewJobDetailsProps } from "../index";

const getLocationDetails = (
  location: HiringEventLocation,
): { technologyRequirements: string; useVip: boolean; isRemote: boolean } => {
  if (location.__typename !== LocationTypeName.PHYSICAL_LOCATION_TYPE_NAME) {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
    const nonPhysicalLocation = location as HiringEventNonPhysicalLocation;
    return {
      isRemote: true,
      technologyRequirements: nonPhysicalLocation.technologyRequirements,
      useVip: nonPhysicalLocation.__typename !== LocationTypeName.VIP_LOCATION_TYPE_NAME,
    };
  } else {
    return {
      isRemote: false,
      technologyRequirements: "",
      useVip: false,
    };
  }
};

export const getPerksDetails = (gt: GettextInterface, hiringEvent: HiringEventData): string[] => {
  const {
    posting: { customPerks, genericPerks },
  } = hiringEvent;
  const eventPerks = [];
  for (const perk of genericPerks) {
    if (perk === "COUPONS_OR_VOUCHERS") {
      eventPerks.push(gt.pgettext("Event Perk - Coupon or vouchers", "Coupon or vouchers"));
    } else if (perk === "COMPANY_SWAG") {
      eventPerks.push(gt.pgettext("Event Perk - Company Swag", "Company swag"));
    } else if (perk === "SNACKS_AND_REFRESHMENTS") {
      eventPerks.push(
        gt.pgettext("Event Perk - Snacks and refreshments", "Snacks and refreshments"),
      );
    }
  }

  return eventPerks.concat(customPerks);
};

export const getIntroHeaderAndContent = (
  introHtml: string,
): { introHeader: string; introContent: string } => {
  const titleAndContent = splitTitleAndContent(introHtml);
  return {
    introHeader: titleAndContent.title,
    introContent: titleAndContent.content,
  };
};

export const hiringEventsViewJobDetailsDataAdapter = (
  gt: GettextInterface,
  hiringEvent: HiringEventData,
): Omit<HiringEventsViewJobDetailsProps, "jobKey"> => {
  const {
    companyName,
    id,
    interviewConfig: { dressCodeDescription, location, requiredItems },
    posting: { companyAboutHTML, directionsHTML, eventRequirements, hiringEventIntroHTML },
  } = hiringEvent;

  return {
    directionsHTML: directionsHTML,
    hiringEventUuid: id,
    requirements: eventRequirements,
    requiredItems: requiredItems,
    dressCodeDescription: dressCodeDescription,
    companyName: companyName,
    companyAboutHTML: companyAboutHTML,
    perks: getPerksDetails(gt, hiringEvent),
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
    ...getLocationDetails(location as HiringEventLocation),
    ...getIntroHeaderAndContent(hiringEventIntroHTML),
  };
};
